"use client";

import { motion } from "motion/react";
import { useLang } from "@/context/lang";
import { useUpper } from "@/lib/upper";
import { Logo } from "@/components/logo";
import { ArrowRight, Mail, Headphones, Globe, Zap, Shield, MapPin, Phone } from "lucide-react";
import Link from "next/link";
import { useState, useEffect } from "react";

function AmericanFlag({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 1235 650" className={className} aria-label="American Flag">
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
        <rect key={i} x="0" y={i * 50} width="1235" height="50" fill={i % 2 === 0 ? "#B22234" : "#FFFFFF"} />
      ))}
      <rect x="0" y="0" width="494" height="350" fill="#3C3B6E" />
      {Array.from({ length: 9 }).map((_, row) => (
        Array.from({ length: row % 2 === 0 ? 6 : 5 }).map((_, col) => (
          <circle key={`${row}-${col}`} cx={45 + col * 82 + (row % 2 === 0 ? 0 : 41)} cy={30 + row * 35} r="11" fill="white" />
        ))
      ))}
    </svg>
  );
}

export function Footer() {
  const { T } = useLang();
  const U = useUpper();
  const [loggedIn, setLoggedIn] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setLoggedIn(!!localStorage.getItem("speakroar-user"));
  }, []);

  const productLinks = [
    { label: T("footer.features"), href: "#ozellikler" },
    { label: T("footer.pricing"), href: "#fiyatlar" },
    { label: T("footer.voice_library"), href: "#sesler" },
    { label: T("footer.comparison"), href: "#karsilastir" },
    { label: T("footer.faq"), href: "#sss" },
  ];

  const voiceLinks = [
    { label: T("footer.turkish_voices"), href: "#sesler" },
    { label: T("footer.english_voices"), href: "#sesler" },
    { label: T("footer.all_languages"), href: "#sesler" },
    { label: T("footer.how_it_works"), href: "#nasil-calisir" },
  ];

  const legalLinks = [
    { label: T("footer.privacy"), href: "/privacy" },
    { label: T("footer.terms"), href: "/terms" },
    { label: T("footer.cookies"), href: "/cookies" },
    { label: T("footer.ccpa"), href: "/ccpa" },
  ];

  return (
    <footer className="relative bg-neutral-950 text-white overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-cyan-500/[0.03] rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-teal-500/[0.03] rounded-full blur-3xl" />
      </div>

      {/* CTA Banner */}
      <div className="relative border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row items-center justify-between gap-6"
          >
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-white">
                {T("footer.cta_title")}
              </h3>
              <p className="text-sm text-neutral-400 dark:text-slate-500 mt-1">
                {T("footer.cta_desc")}
              </p>
            </div>
            <Link
              href={mounted && loggedIn ? "/dashboard" : "/register"}
              className="flex-shrink-0 group flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-semibold text-sm hover:shadow-lg hover:shadow-cyan-500/20 transition-all"
            >
              {mounted && loggedIn ? T("nav.go_studio") : T("nav.signup")}
              <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-8">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-4">
            <Logo size="md" white />
            <p className="text-sm text-neutral-400 dark:text-slate-500 mt-4 leading-relaxed max-w-xs">
              {T("footer.desc")}
            </p>
            <div className="flex flex-wrap gap-3 mt-5">
              {[
                { icon: Zap, text: "2,600+" },
                { icon: Globe, text: "25+" },
                { icon: Shield, text: "SSL" },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/[0.06] text-[10px] font-semibold text-neutral-400 dark:text-slate-500">
                  <b.icon size={10} className="text-cyan-400" /> {b.text}
                </div>
              ))}
            </div>
          </div>

          {/* Product Links */}
          <div className="col-span-1 md:col-span-2">
            <h4 className="text-xs font-bold text-neutral-300 dark:text-slate-600 tracking-wider mb-4">{U(T("footer.product"))}</h4>
            <ul className="space-y-2.5">
              {productLinks.map((l) => (
                <li key={l.label}>
                  <a href={l.href} className="text-sm text-neutral-500 dark:text-white/80 hover:text-cyan-400 transition-colors">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Voice Links */}
          <div className="col-span-1 md:col-span-2">
            <h4 className="text-xs font-bold text-neutral-300 dark:text-slate-600 tracking-wider mb-4">{U(T("footer.voices"))}</h4>
            <ul className="space-y-2.5">
              {voiceLinks.map((l) => (
                <li key={l.label}>
                  <a href={l.href} className="text-sm text-neutral-500 dark:text-white/80 hover:text-cyan-400 transition-colors">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Info */}
          <div className="col-span-2 md:col-span-4">
            <h4 className="text-xs font-bold text-neutral-300 dark:text-slate-600 tracking-wider mb-4">{U(T("footer.contact"))}</h4>
            <div className="space-y-3">
              <a href="mailto:info@speakroar.com" className="flex items-center gap-2.5 text-sm text-neutral-400 dark:text-slate-500 hover:text-cyan-400 transition-colors">
                <div className="w-7 h-7 rounded-lg bg-white/[0.05] flex items-center justify-center flex-shrink-0">
                  <Mail size={13} className="text-cyan-500 dark:text-cyan-400/70" />
                </div>
                info@speakroar.com
              </a>
              <a href="tel:+15054052773" className="flex items-center gap-2.5 text-sm text-neutral-400 dark:text-slate-500 hover:text-cyan-400 transition-colors">
                <div className="w-7 h-7 rounded-lg bg-white/[0.05] flex items-center justify-center flex-shrink-0">
                  <Phone size={13} className="text-cyan-500 dark:text-cyan-400/70" />
                </div>
                {T("footer.phone")}
              </a>
              <div className="flex items-start gap-2.5 text-sm text-neutral-500 dark:text-white/80">
                <div className="w-7 h-7 rounded-lg bg-white/[0.05] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin size={13} className="text-cyan-500 dark:text-cyan-400/70" />
                </div>
                {T("footer.location")}
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {[
                { icon: Headphones, text: T("footer.natural_voices") },
                { icon: Globe, text: T("footer.languages_supported") },
                { icon: Zap, text: T("footer.unlimited_chars") },
              ].map((f) => (
                <div key={f.text} className="flex items-center gap-2.5 text-xs text-neutral-500 dark:text-white/80">
                  <div className="w-6 h-6 rounded-md bg-white/[0.05] flex items-center justify-center flex-shrink-0">
                    <f.icon size={12} className="text-cyan-500 dark:text-cyan-400/70" />
                  </div>
                  {f.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Secure Payment Bar */}
      <div className="relative border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            <Shield size={13} className="text-green-500/60" />
            <span className="text-xs text-neutral-400 dark:text-slate-500 font-medium">{T("footer.secure_checkout")}</span>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Left: Copyright */}
            <div className="flex items-center gap-3">
              <p className="text-xs text-neutral-500 dark:text-white/80">
                &copy; 2026 CreateNano LLC. {T("footer.rights")}
              </p>
            </div>

            {/* Center: Legal links */}
            <div className="flex items-center gap-4 flex-wrap justify-center">
              {legalLinks.map((l) => (
                <Link key={l.label} href={l.href} className="text-[11px] text-neutral-600 dark:text-white/85 hover:text-neutral-400 transition-colors">
                  {l.label}
                </Link>
              ))}
            </div>

            {/* Right: Flag + License Badge */}
            <div className="flex items-center gap-3">
              <motion.div
                animate={{ rotateY: [0, 10, -10, 5, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", repeatDelay: 3 }}
                style={{ perspective: 200 }}
              >
                <AmericanFlag className="w-6 h-4 rounded-[2px] shadow-sm shadow-blue-500/20" />
              </motion.div>
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/20 text-[10px] font-medium text-green-400">
                <Shield size={10} /> {T("footer.licensed")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
